import { animate, stagger } from "motion";
import { splitText } from "motion-plus";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Canonical page header for every dashboard route. Single source of truth
 * for title typography (Geist sans, semibold, tracking-tight), subtitle
 * (muted, sans), entrance animation (motion-plus splitText word-by-word
 * reveal), and layout (left-aligned by default, optional right slot).
 *
 * - `accentWords`: 1-2 words highlighted in the pink accent color per the
 *   brand rule. Case-insensitive substring match against the title.
 * - `subtitle`: Title Case is enforced by caller. This component doesn't
 *   auto-capitalize because some subtitles contain proper nouns / acronyms.
 */
export interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  /** Optional icon glyph — rendered in an accent-tinted pill to the left of the title. */
  icon?: ReactNode;
  /** Slot for CTAs (buttons, filters, etc) pinned to the header's right edge. */
  right?: ReactNode;
  /** Words inside `title` to highlight in accent pink. */
  accentWords?: string[];
  className?: string;
}

function renderAccented(title: string, accentWords?: string[]): ReactNode {
  if (!accentWords || accentWords.length === 0) return title;
  const lowered = accentWords.map((w) => w.toLowerCase());
  return title.split(/(\s+)/).map((chunk, i) => {
    if (/^\s+$/.test(chunk)) return chunk;
    const isAccent = lowered.some((w) => chunk.toLowerCase().includes(w));
    return isAccent ? (
      <span key={i} className="text-accent">
        {chunk}
      </span>
    ) : (
      <span key={i}>{chunk}</span>
    );
  });
}

export function PageHeader({
  title,
  subtitle,
  icon,
  right,
  accentWords,
  className,
}: PageHeaderProps): ReactNode {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || hasAnimated) return;
    let cancelled = false;

    document.fonts.ready.then(() => {
      if (cancelled || !container.isConnected) return;
      container.style.visibility = "visible";
      const el = container.querySelector<HTMLElement>("[data-split-target]");
      if (!el) return;
      try {
        const { words } = splitText(el);
        animate(
          words,
          { opacity: [0, 1], y: [8, 0] },
          { type: "spring", duration: 1.8, bounce: 0, delay: stagger(0.05) },
        );
      } catch {
        // splitText can throw on unmount races — fallback to just showing.
      }
      setHasAnimated(true);
    });

    return () => {
      cancelled = true;
    };
  }, [hasAnimated, title]);

  return (
    <div
      ref={containerRef}
      style={{ visibility: "hidden" }}
      className={cn(
        "flex w-full min-w-0 flex-wrap items-start justify-between gap-x-4 gap-y-3 font-sans",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {icon && (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-lg text-accent">
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h1
            data-split-target
            className="font-sans text-2xl font-semibold tracking-tight text-foreground break-words sm:text-3xl"
          >
            {renderAccented(title, accentWords)}
          </h1>
          {subtitle && (
            <p className="mt-1 max-w-prose font-sans text-sm text-muted-foreground">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {right && (
        <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto sm:shrink-0">
          {right}
        </div>
      )}
      <style>{`
        .split-word { will-change: transform, opacity; }
        [data-split-target] .text-accent,
        [data-split-target] .text-accent * { color: var(--color-accent) !important; }
      `}</style>
    </div>
  );
}
